import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const RETRY_INTERVAL_MS = 100;
const MAX_RETRY_MS = 2000;

function scrollToSectionAnchor(elementId: string): boolean {
  const element = document.getElementById(elementId);
  if (!element) return false;
  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
}

export function useDashboardSectionScroll(): void {
  const location = useLocation();
  const retryTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (retryTimerRef.current !== null) {
      window.clearInterval(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    const hash = location.hash;
    if (!hash.startsWith('#section-')) return;

    const elementId = hash.slice(1);
    const startedAt = Date.now();

    const attemptScroll = () => {
      if (scrollToSectionAnchor(elementId)) {
        if (retryTimerRef.current !== null) {
          window.clearInterval(retryTimerRef.current);
          retryTimerRef.current = null;
        }
        return;
      }

      if (Date.now() - startedAt >= MAX_RETRY_MS && retryTimerRef.current !== null) {
        window.clearInterval(retryTimerRef.current);
        retryTimerRef.current = null;
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[menuSearch] Section anchor not found after retries: #${elementId}`);
        }
      }
    };

    attemptScroll();
    retryTimerRef.current = window.setInterval(attemptScroll, RETRY_INTERVAL_MS);

    return () => {
      if (retryTimerRef.current !== null) {
        window.clearInterval(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [location.pathname, location.hash]);
}
